import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { MapPin, Hospital, Trees, ShoppingBag, Heart, Cat, Home } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface AddPlaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
  onPlaceAdded?: () => void;
}

const PLACE_CATEGORIES = [
  { value: 'veterinari', label: '🏥 Veterinari e cliniche', icon: Hospital },
  { value: 'parchi', label: '🌳 Parchi e aree verdi', icon: Trees },
  { value: 'negozi', label: '🛍️ Negozi per animali', icon: ShoppingBag },
  { value: 'adozioni', label: '❤️ Centri adozione', icon: Heart },
  { value: 'colonie-feline', label: '🐱 Colonie feline', icon: Cat },
  { value: 'canili', label: '🏠 Canili', icon: Home }
];

export const AddPlaceModal = ({ isOpen, onClose, latitude, longitude, onPlaceAdded }: AddPlaceModalProps) => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [category, setCategory] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Devi essere autenticato per aggiungere un luogo');
      return;
    }

    if (!title.trim() || !category) {
      toast.error('Titolo e categoria sono obbligatori');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase
        .from('locations')
        .insert({
          title: title.trim(),
          description: description.trim() || null,
          address: address.trim() || null,
          category,
          latitude,
          longitude,
          is_public: isPublic,
          user_id: user.id
        });

      if (error) throw error;

      toast.success('Luogo aggiunto con successo!');
      onPlaceAdded?.();
      onClose();
      
      // Reset form
      setTitle('');
      setDescription('');
      setAddress('');
      setCategory('');
      setIsPublic(true);
    } catch (error) {
      console.error('Error adding place:', error);
      toast.error('Errore durante l\'aggiunta del luogo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setAddress('');
    setCategory('');
    setIsPublic(true);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Aggiungi Luogo
          </DialogTitle>
          <DialogDescription>
            Segnala un luogo utile per la community pet. Coordinate: {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titolo *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Nome del luogo..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Categoria *</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona una categoria" />
              </SelectTrigger>
              <SelectContent>
                {PLACE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Indirizzo</Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Via, numero civico, città..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrivi il luogo, orari, servizi..."
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is-public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
            />
            <Label htmlFor="is-public" className="text-sm">
              Rendi pubblico questo luogo
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annulla
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Aggiungendo...' : 'Aggiungi Luogo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};